import { PostMessageSender, PostMessageHandlers, PostMessageReply } from '../Shared/PostMessage';
import {
  GetMeetingIdMessageType,
  SetMeetingIdMessage,
  SetMeetingIdMessageType,
  GetMeetingIdMessage,
  GetMeetingIdReplyMessage,
  GetSeriesIdMessageType,
  GetSeriesIdReplyMessage,
  SetSeriesIdMessage,
  SetSeriesIdMessageType,
  GetSeriesIdMessage
} from '../MessageTypes/MeetingInfoMessageTypes';
import { MeetingInfo, MeetingInfoProvider } from '@fluidx/office-fluid-types';
import { IFluidObject } from '@fluidframework/core-interfaces';

/**
 * Initializes everything needed to handle the MeetingInfo interface outside the iframe
 * @param sender how to send messages to the inside of the iframe
 * @returns A dummy component with just this interface and the set of message handlers this interface is expecting
 */
export function getHostSideMeetingInfo(sender: PostMessageSender): { component: MeetingInfo } {
  return { component: new HostSideMeetingInfoProxy(sender) };
}

class HostSideMeetingInfoProxy implements MeetingInfoProvider {
  constructor(private sender: PostMessageSender) {}

  public get MeetingInfo() {
    return this;
  }

  getMeetingId(): Promise<string> {
    const message: GetMeetingIdMessage = {
      type: GetMeetingIdMessageType
    };
    return this.sender.sendWithReturn(message).then((response: PostMessageReply) => {
      if (response.error) {
        throw new Error(`Could not get meetingId. Error: ${response.error}`);
      }
      return (response as GetMeetingIdReplyMessage).payload;
    });
  }

  setMeetingId(meetingId: string): Promise<void> {
    const message: SetMeetingIdMessage = {
      type: SetMeetingIdMessageType,
      payload: { meetingId }
    };
    return this.sender.sendWithReturn(message).then((response: PostMessageReply) => {
      if (response.error) {
        throw new Error(`Could not set meetingId. Error: ${response.error}`);
      }
    });
  }

  getSeriesId(): Promise<string> {
    const message: GetSeriesIdMessage = {
      type: GetSeriesIdMessageType
    };
    return this.sender.sendWithReturn(message).then((response: PostMessageReply) => {
      if (response.error) {
        throw new Error(`Could not get seriesId. Error: ${response.error}`);
      }
      return (response as GetSeriesIdReplyMessage).payload;
    });
  }

  setSeriesId(seriesId: string): Promise<void> {
    const message: SetSeriesIdMessage = {
      type: SetSeriesIdMessageType,
      payload: { seriesId }
    };
    return this.sender.sendWithReturn(message).then((response: PostMessageReply) => {
      if (response.error) {
        throw new Error(`Could not set seriesId. Error: ${response.error}`);
      }
    });
  }
}

/**
 * Initializes everything needed to handle the MeetingInfo interface inside the iframe
 * @param component the actual component with the real implementation of the interface
 * @param sender how to send messages to the outside of the iframe
 */
export function getGuestSideMeetingInfo(
  component: IFluidObject,
  sender: PostMessageSender
): PostMessageHandlers<string> | undefined {
  const meetingInfo = component.MeetingInfo;

  if (!meetingInfo) {
    return undefined;
  }

  return {
    [GetMeetingIdMessageType]: async (message) => {
      try {
        const meetingId = await meetingInfo.getMeetingId();
        sender.sendReply(message, { payload: meetingId });
      } catch {
        sender.sendReply(message, { error: true });
      }
    },
    [SetMeetingIdMessageType]: async (message) => {
      try {
        await meetingInfo.setMeetingId((message as SetMeetingIdMessage).payload.meetingId);
        sender.sendReply(message, {});
      } catch {
        sender.sendReply(message, { error: true });
      }
    },
    [GetSeriesIdMessageType]: async (message) => {
      try {
        const seriesId = await meetingInfo.getSeriesId();
        sender.sendReply(message, { payload: seriesId });
      } catch {
        sender.sendReply(message, { error: true });
      }
    },
    [SetSeriesIdMessageType]: async (message) => {
      try {
        await meetingInfo.setSeriesId((message as SetSeriesIdMessage).payload.seriesId);
        sender.sendReply(message, {});
      } catch {
        sender.sendReply(message, { error: true });
      }
    }
  };
}
